"use client";

import { UserMissionItemsModel } from "@api/models";
import { MissionListDummyContent } from "./MissionListDummyContent";
import { MissionListItem } from "./MissionListItem";
import { useMissions } from "app/hooks";
import { useSyncExternalStore } from "react";
import { sessionStore } from "@lib/sessionStore";
import { ErrorDisplay } from "./ErrorDisplay";
import { useSearchParams } from "next/navigation";
import Link from "next/link";
import { GLOBALS } from "config";

type ListName = keyof Omit<
  UserMissionItemsModel,
  "startDayOfWeeklyMission" | "isStartDayOfWeeklyMission"
>;

const types: { title: string; listName: ListName }[] = [
  { title: "チュートリアル", listName: "tutorialMissionList" },
  { title: "キャンペーン", listName: "campaignMissionList" },
  { title: "デイリー", listName: "dailyMissionList" },
  { title: "ウィークリー", listName: "weeklyMissionList" },
  { title: "マンスリー", listName: "monthlyMissionList" },
  { title: "イヤリー", listName: "yearlyMissionList" },
  { title: "その他", listName: "noneMissionList" },
];

/** 日曜始まりの曜日の配列 */
const daysListStartWithSunday = ["日", "月", "火", "水", "木", "金", "土"];

const loggedOutGachaMission = {
  missions: {
    dailyMissionList: [
      {
        title: "ガチャを1回まわす",
        endDate: 0,
        achievementCount: 0,
        targetAchievementCount: 1,
        isAchieved: false,
        achievedDate: 0,
        rewardTicketNumber: 1,
        linkDestination: "/gacha",
      },
    ],
  },
};

export function MissionList({ isPopUp = false }: { isPopUp?: boolean }) {
  const { sessionStatus } = useSyncExternalStore(
    sessionStore.subscribe,
    sessionStore.getSnapshot,
    sessionStore.getServerSnapshot,
  );
  const params = useSearchParams();
  const isOngoing = params.get("missionStatus") === "1" ? false : true;
  const {
    results: loadedMissions,
    isLoading: isMissionLoading,
    error,
  } = useMissions(sessionStatus === "authenticated" && GLOBALS.HAS_MISSION, !isOngoing);

  // ログイン状態かつロード失敗時はエラー表示して終了
  if (error && sessionStatus === "authenticated") {
    return <ErrorDisplay message="ミッション情報の取得時にエラーが発生しました" />;
  }

  let missions = loadedMissions;
  // 非ログインならガチャミッションを表示（ガチャのあるサイトののみ）
  if (sessionStatus === "unauthenticated") {
    missions = GLOBALS.HAS_GACHA_PAGE
      ? loggedOutGachaMission
      : {
          missions: {},
        };
  }

  //ロード中判定
  const isLoading = isMissionLoading || sessionStatus === "loading";

  const hasMissions: boolean = types.some((t) => {
    if (isLoading || error) return true;
    const missionList = missions.missions[t.listName];
    return missionList?.length;
  });

  return (
    <div className="mission-list-container">
      {!isPopUp && sessionStatus === "authenticated" && (
        <ul className="mission-list-status">
          <li className={"mission-list-status-tab" + (isOngoing ? " mode-selected" : "")}>
            <Link href="/mission?missionStatus=0" className="mission-list-status-tab-link">
              進行中
            </Link>
          </li>
          <li className={"mission-list-status-tab" + (isOngoing ? "" : " mode-selected")}>
            <Link href="/mission?missionStatus=1" className="mission-list-status-tab-link">
              達成済み
            </Link>
          </li>
        </ul>
      )}
      {isLoading && <div className="g-loading">loading</div>}
      {!isLoading && (
        <>
          {hasMissions &&
            types.map((type) => {
              const missionList = missions.missions[type.listName];
              if (
                missions.missions[type.listName] !== undefined &&
                missions.missions[type.listName]?.length !== 0
              ) {
                return (
                  <div className="mission-list" key={type.listName}>
                    {!isPopUp && (
                      <h3 className="mission-list-h">
                        {type.title}
                        {type.listName === "weeklyMissionList" &&
                          missions.missions.startDayOfWeeklyMission &&
                          `（毎週${daysListStartWithSunday[missions.missions.startDayOfWeeklyMission]}曜0時リセット）`}
                      </h3>
                    )}
                    <div className="mission-list-items">
                      {missionList?.map((mission, idx) => {
                        return (
                          <MissionListItem
                            mission={mission}
                            key={type.listName + "-" + idx}
                            startDayOfWeeklyMission={
                              type.listName === "weeklyMissionList"
                                ? missions.missions.startDayOfWeeklyMission
                                : undefined
                            }
                            isPopUp={isPopUp}
                          />
                        );
                      })}
                    </div>
                  </div>
                );
              }
            })}
          {!hasMissions && (
            <p className="mission-no-items">
              {isOngoing ? "現在ミッションはありません" : "達成済みのミッションはありません"}
            </p>
          )}
          {sessionStatus === "unauthenticated" && <MissionListDummyContent />}
        </>
      )}
    </div>
  );
}
